import { inject, Injectable } from '@angular/core';
import { NgxZendeskWebwidgetService } from '@nitsanzo/ngx-zendesk-webwidget';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import { ConfigService } from './config.service';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { ReportComment, ReportRequest, Requester, ResourceIssue } from '../models/report-request';
import { LocaleService } from './locale.service';
import { Util } from '../util';
import { CookiesService } from '../_core/state/dialogs/cookies/service/cookies.service';

declare global {
  interface Window {
    zESettings: {
      webWidget: {
        color: { theme: string };
        position: { horizontal: string };
        launcher: {
          chatLabel: {
            ar: 'الدعم';
            ca: 'Suport';
            de: 'Die Unterstützung';
            en: 'Support';
            es: 'Soporte';
            eu: 'Laguntza';
            fr: 'Assistance';
            gl: 'Apoiar';
            he: 'תמיכה';
            nl: 'Steun';
            pt: 'Suporte';
            tr: 'Destek';
            uk: 'Apoiar';
            va: 'Suport';
            zh: '支持';
            '*': 'Support';
          };
        };
        contactOptions: {
          chatLabelOnline: {
            ar: 'الدعم';
            ca: 'Suport';
            de: 'Die Unterstützung';
            en: 'Support';
            es: 'Soporte';
            eu: 'Laguntza';
            fr: 'Assistance';
            gl: 'Apoiar';
            he: 'תמיכה';
            nl: 'Steun';
            pt: 'Suporte';
            tr: 'Destek';
            uk: 'Apoiar';
            va: 'Suport';
            zh: '支持';
            '*': 'Support';
          };
        };
      };
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class SupportService {
  textDirection: string;

  private readonly cookiesService = inject(CookiesService);

  constructor(
    private logger: LoggerService,
    private zendeskService: NgxZendeskWebwidgetService,
    private configService: ConfigService,
    private apiService: ApiService,
    private localeService: LocaleService,
    private authService: AuthService
  ) {
  }

  init(locale?: string) {
    if (!this.configService.get().showZendesk) {
      return;
    }

    if (!this.cookiesService.hasConsentForCookie('zendesk')) {
      this.logger.warn('No Zendesk for you');
      return;
    }

    this.textDirection = this.localeService.getTextDirection();
    this.initSettingsZendesk();
    this.zendeskService.initZendesk().then(() => {
      this.zendeskService.zE('webWidget', 'setLocale', locale);

      if (this.authService.isAuthenticated()) {
        const user = this.authService.getLoggedUser();

        this.zendeskService.zE('webWidget', 'identify', {
          name: user.name,
          email: user.email,
        });

        this.zendeskService.zE('webWidget', 'prefill', {
          name: { value: user.name },
          email: { value: user.email },
        });
      }
    });
  }

  initSettingsZendesk() {
    window['zESettings'] = {
      webWidget: {
        launcher: {
          chatLabel: {
            ar: 'الدعم',
            ca: 'Suport',
            de: 'Die Unterstützung',
            en: 'Support',
            es: 'Soporte',
            eu: 'Laguntza',
            fr: 'Assistance',
            gl: 'Apoiar',
            he: 'תמיכה',
            nl: 'Steun',
            pt: 'Suporte',
            tr: 'Destek',
            uk: 'Apoiar',
            va: 'Suport',
            zh: '支持',
            '*': 'Support',
          },
        },
        contactOptions: {
          chatLabelOnline: {
            ar: 'الدعم',
            ca: 'Suport',
            de: 'Die Unterstützung',
            en: 'Support',
            es: 'Soporte',
            eu: 'Laguntza',
            fr: 'Assistance',
            gl: 'Apoiar',
            he: 'תמיכה',
            nl: 'Steun',
            pt: 'Suporte',
            tr: 'Destek',
            uk: 'Apoiar',
            va: 'Suport',
            zh: '支持',
            '*': 'Support',
          },
        },
        color: { theme: '#fff' },
        position: {
          horizontal: this.textDirection === 'rtl' ? 'right' : 'left',
        },
      },
    };
  }

  report(wrongMetadata: boolean, wrongCover: boolean, playingIssues: boolean, details: string): Observable<any> {
    const issue: ResourceIssue = {
      resourceUrl: window.location.href,
      wrongMetadata,
      wrongCover,
      playingIssues,
      details,
    };

    const request: ReportRequest = {
      requester: this.buildRequester(),
      comment: this.buildComment(issue),
      subject: '[OPAC] Report a problem',
      custom_fields: [
        {
          id: 23937516,
          value: 'OdiloTK',
        },
      ],
    };

    return this.apiService.reportToZendesk(request);
  }

  private buildRequester(): Requester {
    // TODO: we're going to need this in the near future
    // if (this.authService.isAuthenticated()) {
    //   const user = this.authService.getLoggedUser();
    //
    //   return {
    //     name: user.name,
    //     email: user.email,
    //     locale: this.localeService.language
    //   };
    // }
    //
    // return {
    //   name: 'Anonymous',
    //   locale: this.localeService.language
    // };

    return {
      name: 'ODILO Support',
      email: 'enterprise@odilo.us',
    };
  }

  private buildComment(issue: ResourceIssue): ReportComment {
    const cleanedIssue: ResourceIssue = Object.entries(issue).reduce((a, [k, v]) => (v ? ((a[k] = v), a) : a), {});

    const body = Object.entries(cleanedIssue)
      .map(([k, v]) => `${Util.unCamelCase(k)}: ${v}`)
      .join('\n');

    return { body, created_at: new Date() };
  }
}
